import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import Navigation from '../components/Navigation';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Send, MessageCircle, Circle, ArrowLeft } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WS_URL = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');

const MessagesPage = () => {
  const { user, token } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [mobileView, setMobileView] = useState('list'); // 'list' | 'chat'
  const wsRef = useRef(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchConversations();
    connectWebSocket();
    return () => {
      if (wsRef.current) wsRef.current.close();
    };
  }, []);

  useEffect(() => {
    if (activeConversation) fetchMessages(activeConversation.user_id);
  }, [activeConversation]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const connectWebSocket = () => {
    if (!token) return;
    try {
      const ws = new WebSocket(`${WS_URL}/ws/chat/${token}`);
      ws.onmessage = (event) => {
        const msg = JSON.parse(event.data);
        setMessages(prev => {
          if (prev.find(m => m.id === msg.id)) return prev;
          return [...prev, msg];
        });
        updateConversationPreview(msg);
      };
      ws.onclose = () => {
        setTimeout(connectWebSocket, 3000);
      };
      wsRef.current = ws;
    } catch (e) {
      console.error('WebSocket connection failed', e);
    }
  };

  const updateConversationPreview = (msg) => {
    setConversations(prev => prev.map(c =>
      (c.user_id === msg.sender_id || c.user_id === msg.recipient_id)
        ? { ...c, last_message: msg.content, last_message_at: msg.created_at }
        : c
    ));
  };

  const fetchConversations = async () => {
    try {
      const res = await axios.get(`${API}/messages`);
      const msgList = res.data;
      const convMap = {};
      msgList.forEach(msg => {
        const otherId = msg.sender_id === user?.id ? msg.recipient_id : msg.sender_id;
        const otherName = msg.sender_id === user?.id ? (msg.recipient_name || 'User') : msg.sender_name;
        if (!convMap[otherId] || msg.created_at > convMap[otherId].last_message_at) {
          convMap[otherId] = {
            user_id: otherId,
            user_name: otherName,
            last_message: msg.content,
            last_message_at: msg.created_at
          };
        }
      });
      setConversations(Object.values(convMap).sort((a, b) => b.last_message_at?.localeCompare(a.last_message_at)));
    } catch (error) {
      console.error('Failed to fetch conversations', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (otherUserId) => {
    try {
      const res = await axios.get(`${API}/messages/conversation/${otherUserId}`);
      setMessages(res.data);
    } catch (error) {
      console.error('Failed to fetch messages', error);
    }
  };

  const sendMessage = (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !activeConversation) return;

    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        recipient_id: activeConversation.user_id,
        content: newMessage.trim()
      }));
    } else {
      axios.post(`${API}/messages`, {
        recipient_id: activeConversation.user_id,
        content: newMessage.trim()
      }).then(() => fetchMessages(activeConversation.user_id));
    }
    setNewMessage('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <Navigation />
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-6">
        <h1 className="text-2xl md:text-3xl font-heading font-bold text-primary mb-6" data-testid="messages-title">
          Messages
        </h1>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 h-[calc(100vh-200px)]">
          {/* Conversations List */}
          <Card className={`md:col-span-1 ${mobileView === 'chat' ? 'hidden md:flex md:flex-col' : 'flex flex-col'}`}>
            <CardHeader className="py-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <MessageCircle className="w-4 h-4" /> Conversations
              </CardTitle>
            </CardHeader>
            <ScrollArea className="h-[calc(100%-60px)]">
              <CardContent className="p-0">
                {conversations.length === 0 ? (
                  <p className="p-4 text-center text-muted-foreground text-sm">No conversations yet</p>
                ) : (
                  conversations.map(conv => (
                    <button
                      key={conv.user_id}
                      onClick={() => { setActiveConversation(conv); setMobileView('chat'); }}
                      data-testid={`conversation-${conv.user_id}`}
                      className={`w-full text-left p-3 border-b transition-colors ${
                        activeConversation?.user_id === conv.user_id
                          ? 'bg-secondary/10 border-l-2 border-l-secondary'
                          : 'hover:bg-slate-50'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">
                          {conv.user_name?.charAt(0)?.toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{conv.user_name}</p>
                          <p className="text-xs text-muted-foreground truncate">{conv.last_message}</p>
                        </div>
                      </div>
                    </button>
                  ))
                )}
              </CardContent>
            </ScrollArea>
          </Card>

          {/* Chat Area */}
          <Card className={`md:col-span-2 flex flex-col ${mobileView === 'list' ? 'hidden md:flex' : 'flex'}`}>
            {activeConversation ? (
              <>
                <CardHeader className="py-3 border-b">
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="icon" className="md:hidden h-8 w-8" onClick={() => setMobileView('list')}>
                      <ArrowLeft className="w-4 h-4" />
                    </Button>
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">
                      {activeConversation.user_name?.charAt(0)?.toUpperCase()}
                    </div>
                    <CardTitle className="text-base">{activeConversation.user_name}</CardTitle>
                    <Circle className="w-2 h-2 fill-green-500 text-green-500" />
                  </div>
                </CardHeader>
                <ScrollArea className="flex-1 p-4">
                  <div className="space-y-3">
                    {messages.map(msg => (
                      <div
                        key={msg.id}
                        className={`flex ${msg.sender_id === user?.id ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[70%] px-3 py-2 rounded-lg text-sm ${
                            msg.sender_id === user?.id
                              ? 'bg-secondary text-secondary-foreground'
                              : 'bg-slate-100 text-primary'
                          }`}
                        >
                          <p>{msg.content}</p>
                          <p className="text-[10px] opacity-60 mt-1">
                            {new Date(msg.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>
                </ScrollArea>
                <form onSubmit={sendMessage} className="p-3 border-t flex gap-2" data-testid="message-form">
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type a message..."
                    className="flex-1"
                    data-testid="message-input"
                  />
                  <Button type="submit" size="icon" data-testid="send-message-button">
                    <Send className="w-4 h-4" />
                  </Button>
                </form>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-muted-foreground">
                <div className="text-center">
                  <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Select a conversation to start chatting</p>
                </div>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default MessagesPage;
